import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw, Upload, ShieldCheck, ArrowRightLeft } from "lucide-react";

async function uploadFile(file) {
  const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
  const { data } = await api.post("/uploads", { name: file.name, content_base64: b64, content_type: file.type });
  return data;
}

export default function ReKyc() {
  const { user } = useAuth();
  if (user.role === "compliance" || user.role === "operations" || user.role === "administrator") return <StaffReview />;
  return <CustomerReKyc />;
}

function Field({ label, children }) { return <div><Label className="mb-1 block text-sm">{label}</Label>{children}</div>; }
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function YesNo({ value, onChange, testid }) {
  return <RadioGroup value={value || ""} onValueChange={onChange} className="flex gap-4">
    {["yes", "no"].map((o) => <label key={o} className="flex items-center gap-2 text-sm cursor-pointer capitalize"><RadioGroupItem value={o} data-testid={`${testid}-${o}`} /> {o}</label>)}
  </RadioGroup>;
}

function CustomerReKyc() {
  const [rk, setRk] = useState(undefined);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/rekyc/config").then((r) => setCfg(r.data));
    api.post("/rekyc").then((r) => setRk(r.data)).catch((e) => { setRk(null); toast.error(apiErr(e)); });
  }, []);
  if (rk === undefined || !cfg) return <Spinner label="Loading your current details…" />;
  if (rk === null) return <div className="max-w-2xl mx-auto"><PageHeader overline="Re-KYC" title="Periodic KYC refresh" testid="rekyc-header" /><Card className="p-6"><EmptyState title="No approved record to refresh" hint="Complete onboarding and approval first; Re-KYC starts from your existing approved details." /></Card></div>;

  const submitted = ["in_review", "approved", "rejected", "information_requested"].includes(rk.status);
  const prior = rk.prior_snapshot || {};
  const pc = rk.proposed_changes || {};
  const ans = rk.answers || {};
  const setPc = (k, v) => setRk({ ...rk, proposed_changes: { ...pc, [k]: v } });
  const setAns = (k, v) => setRk({ ...rk, answers: { ...ans, [k]: v } });
  const setUpload = (k, meta) => setRk({ ...rk, uploads: { ...(rk.uploads || {}), [k]: meta } });
  const save = () => api.put(`/rekyc/${rk.id}`, { proposed_changes: pc, answers: ans, uploads: rk.uploads, pep_confirm: rk.pep_confirm, us_person: rk.us_person, declaration: rk.declaration, signature: rk.signature }).then((r) => setRk(r.data));
  const submit = async () => {
    if (!rk.declaration || !rk.signature?.data) { toast.error("Please tick the declaration and sign"); return; }
    setBusy(true);
    try { await save(); const r = await api.post(`/rekyc/${rk.id}/submit`); setRk(r.data); toast.success(`Re-KYC submitted — ${r.data.diff.length} change(s) proposed, pending staff approval`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (submitted) return (
    <div className="max-w-2xl mx-auto">
      <PageHeader overline="Re-KYC" title="Refresh status" testid="rekyc-header" />
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3"><span className="font-medium">Periodic KYC refresh</span><StatusBadge status={rk.status} testid="rekyc-status" /></div>
        <p className="text-sm text-muted-foreground mb-3">Your proposed changes are held separately and take effect only after staff approval. Your previous approved details remain unchanged until then.</p>
        {(rk.diff || []).length > 0 && <DiffTable diff={rk.diff} />}
      </Card>
    </div>
  );

  const FileBtn = ({ k, label }) => { const up = async (file) => { try { const u = await uploadFile(file); setUpload(k, u); toast.success(`Uploaded ${u.name}`); } catch (e) { toast.error(apiErr(e)); } }; return (<div><input type="file" id={`up-${k}`} className="hidden" onChange={(e) => e.target.files[0] && up(e.target.files[0])} data-testid={`upload-${k}`} /><Button size="sm" variant="outline" className="gap-1" onClick={() => document.getElementById(`up-${k}`).click()}><Upload className="h-4 w-4" /> {(rk.uploads || {})[k]?.name ? `Uploaded: ${(rk.uploads || {})[k].name}` : label}</Button></div>); };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader overline="Re-KYC" title="Periodic KYC refresh" subtitle="We've pre-filled your details on record. Tell us what changed — proposed changes are reviewed by staff before taking effect." testid="rekyc-header" />
      <Card className="p-5 sm:p-6 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-2"><RefreshCw className="h-4 w-4 text-gold" /><h3 className="font-semibold">Your details on record</h3></div>
          <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
            <div>Name: <span className="text-foreground">{prior.name || "—"}</span></div>
            <div>Email: <span className="text-foreground">{prior.email || "—"}</span></div>
            <div>Nationality: <span className="text-foreground">{prior.nationality || "—"}</span></div>
            <div>Residence: <span className="text-foreground">{prior.residence || "—"}</span></div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm">Has your residential address changed?</Label>
            <YesNo value={ans.address_changed} onChange={(v) => setAns("address_changed", v)} testid="q-address" />
            {ans.address_changed === "yes" && <div className="mt-2 space-y-2"><Input placeholder="New residential address" value={pc.address || ""} onChange={(e) => setPc("address", e.target.value)} data-testid="rk-address" /><FileBtn k="address_proof" label="Upload address proof" /></div>}
          </div>
          <div>
            <Label className="text-sm">Has your employment / occupation changed?</Label>
            <YesNo value={ans.employment_changed} onChange={(v) => setAns("employment_changed", v)} testid="q-employment" />
            {ans.employment_changed === "yes" && <Grid><Field label="Occupation"><Input value={pc.occupation || ""} onChange={(e) => setPc("occupation", e.target.value)} data-testid="rk-occupation" /></Field><Field label="Employer"><Input value={pc.employer || ""} onChange={(e) => setPc("employer", e.target.value)} /></Field></Grid>}
          </div>
          <div>
            <Label className="text-sm">Material change in financial details / source of wealth?</Label>
            <YesNo value={ans.financial_changed} onChange={(v) => setAns("financial_changed", v)} testid="q-financial" />
            {ans.financial_changed === "yes" && <div className="mt-2 space-y-2"><Input placeholder="Updated annual income (USD band)" value={pc.annual_income_usd || ""} onChange={(e) => setPc("annual_income_usd", e.target.value)} data-testid="rk-income" /><Input placeholder="Updated source of wealth" value={pc.source_of_wealth || ""} onChange={(e) => setPc("source_of_wealth", e.target.value)} /><FileBtn k="financial_proof" label="Upload supporting document (optional)" /></div>}
          </div>
          <div>
            <Label className="text-sm">Has your tax residency changed?</Label>
            <YesNo value={ans.tax_changed} onChange={(v) => setAns("tax_changed", v)} testid="q-tax" />
            {ans.tax_changed === "yes" && <Grid><Field label="New country of tax residence"><Input value={pc.tax_residence || ""} onChange={(e) => setPc("tax_residence", e.target.value)} data-testid="rk-taxcountry" /></Field><Field label="TIN"><Input value={pc.tin || ""} onChange={(e) => setPc("tin", e.target.value)} /></Field></Grid>}
          </div>
          <Field label="Confirm your PEP status"><RadioGroup value={rk.pep_confirm || ""} onValueChange={(v) => setRk({ ...rk, pep_confirm: v })} className="flex gap-4">{["Not a PEP", "PEP / close associate"].map((o) => <label key={o} className="flex items-center gap-2 text-sm cursor-pointer"><RadioGroupItem value={o} data-testid={`pep-${o.includes("Not") ? "no" : "yes"}`} /> {o}</label>)}</RadioGroup></Field>
          <Field label="Are you a US citizen / US tax resident?"><YesNo value={rk.us_person} onChange={(v) => setRk({ ...rk, us_person: v })} testid="rk-us" /></Field>
        </div>

        <div className="border-t border-border pt-4 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={!!rk.declaration} onCheckedChange={(v) => setRk({ ...rk, declaration: v })} data-testid="rk-declaration" /><span className="text-sm">I confirm the above information is accurate and up to date.</span></label>
          <Field label="Signature (type full name to acknowledge)"><Input value={rk.signature?.data || ""} onChange={(e) => setRk({ ...rk, signature: { data: e.target.value, ts: new Date().toISOString() } })} placeholder="Type your full name" data-testid="rk-signature" /></Field>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => save().then(() => toast.success("Draft saved"))} data-testid="rk-save">Save draft</Button>
            <Button onClick={submit} disabled={busy} className="gap-1" data-testid="rk-submit"><ShieldCheck className="h-4 w-4" /> Submit refresh</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function DiffTable({ diff }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid="rekyc-diff">
      <Table>
        <TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Before</TableHead><TableHead>After</TableHead></TableRow></TableHeader>
        <TableBody>{diff.map((d, i) => (<TableRow key={i}><TableCell className="capitalize">{d.field.replace(/_/g, " ")}</TableCell><TableCell className="text-muted-foreground">{String(d.before ?? "—")}</TableCell><TableCell className="text-emerald-700 font-medium">{String(d.after ?? "—")}</TableCell></TableRow>))}</TableBody>
      </Table>
    </div>
  );
}

function StaffReview() {
  const [queue, setQueue] = useState(null);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/rekyc/queue").then((r) => setQueue(r.data));
  useEffect(() => { load(); }, []);
  if (!queue) return <Spinner />;
  const decide = async (action) => {
    try { await api.post(`/rekyc/${active.id}/decision`, { action, reason }); toast.success(`Re-KYC ${action}d`); setActive(null); setReason(""); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div>
      <PageHeader overline="Re-KYC" title="Re-KYC review queue" subtitle="Approve applies proposed changes to the customer record and preserves the previous snapshot in history." testid="rekyc-header" />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          {queue.length === 0 ? <EmptyState title="No re-KYC submissions" hint="Submitted refreshes appear here for review." /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Changes</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{queue.map((r) => (
                <TableRow key={r.id} data-testid={`rekyc-row-${r.id}`}><TableCell>{r.customer_name}</TableCell><TableCell>{(r.diff || []).length}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => setActive(r)} data-testid={`rekyc-open-${r.id}`}>Review</Button></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          )}
        </Card>
        {active && (
          <Card className="p-5 space-y-4" data-testid="rekyc-detail">
            <div className="flex items-center gap-2"><ArrowRightLeft className="h-4 w-4 text-gold" /><h3 className="font-semibold">{active.customer_name} — proposed changes</h3></div>
            {(active.diff || []).length ? <DiffTable diff={active.diff} /> : <p className="text-sm text-muted-foreground">No field changes (confirmation-only refresh).</p>}
            <Textarea placeholder="Decision reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rekyc-reason" />
            <div className="flex gap-2">
              <Button onClick={() => decide("approve")} data-testid="rekyc-approve" className="gap-1"><ShieldCheck className="h-4 w-4" /> Approve & apply</Button>
              <Button variant="outline" onClick={() => decide("reject")} data-testid="rekyc-reject">Reject</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
